import React, { useEffect, useMemo, useRef, useState } from 'react'
import { View, StyleSheet, Platform } from 'react-native'
import { Layout, Text, Icon, Spinner, useTheme } from '@ui-kitten/components'
import * as Updates from 'expo-updates'
import { useTranslation } from 'react-i18next'
import { usePostHog } from 'posthog-react-native'
import { APP_VERSION } from '../hooks/useVersionCheck'
import { bundleFloorProperties, runningBundleIdentity } from '../hooks/useBundleGate'
import { waitForWritesToDrain } from '../global/API/writesInFlight'
import { LIVE_ROLLBACK_DRAIN_CAP_MS, performLiveRollback } from '../global/Firebase/orgReadCutoverWatch'
import { reloadApp } from '../global/Firebase/reloadApp'
import { withTimeout } from '../utils/withTimeout'
import { clearBackoffRecordIf, writeBackoffRecord } from '../utils/bundleLockStore'
import { bundleGateProcess, markBundleLockoutShown } from '../utils/bundleIdentity'
import {
  runBundleUpdateAttempt,
  type BundleAttemptOutcome,
} from '../utils/bundleUpdateAttempt'

/** No step of the attempt may hang: the lock lives exactly as long as the attempt. */
export const UPDATE_CHECK_TIMEOUT_MS = 30 * 1000
export const UPDATE_FETCH_TIMEOUT_MS = 2 * 60 * 1000
const TELEMETRY_FLUSH_TIMEOUT_MS = 2000
/**
 * Once a restart is ISSUED the lock is never handed back by a timer, on either platform: an issued
 * reloadAsync / location.reload cannot be called back, and one that lands after the lock was released
 * would restart a till under a half-rung cart. A restart that fails says so (it rejects / throws); one
 * that neither happens nor fails is left to a human — after this long the screen says to close and
 * reopen the app, and the relaunch is not locked again (the 'restarted' back-off is already written).
 */
export const RESTART_HINT_MS = 15 * 1000
/** No storage access may hold the lock: every read/write of the lock's records is bounded by this. */
const STORAGE_TIMEOUT_MS = 2000

const timeoutAfter = <T,>(promise: Promise<T> | T, ms: number, what: string): Promise<T> =>
  withTimeout(promise, ms, () => new Error(`${what} timed out after ${ms}ms`))

type RestartResult = 'restart-failed' | 'cancelled' | 'vetoed'

// expo-updates is ONE per process, so its in-flight check/download is too: a call made while one is
// running JOINS it rather than starting a second call expo-updates would queue or reject. The shared
// call carries its OWN timeout and is forgotten when it expires — a native call that never settles is
// abandoned then, so it can hold one try, never every later attempt of the process.
let pendingCheck: Promise<{ isAvailable: boolean }> | null = null
let pendingFetch: Promise<unknown> | null = null
const joinCheck = (): Promise<{ isAvailable: boolean }> => {
  if (!pendingCheck) {
    pendingCheck = timeoutAfter(Updates.checkForUpdateAsync(), UPDATE_CHECK_TIMEOUT_MS, 'update check').finally(
      () => {
        pendingCheck = null
      }
    )
  }
  return pendingCheck
}
const joinFetch = (): Promise<unknown> => {
  if (!pendingFetch) {
    pendingFetch = timeoutAfter(Updates.fetchUpdateAsync(), UPDATE_FETCH_TIMEOUT_MS, 'update download').finally(
      () => {
        pendingFetch = null
      }
    )
  }
  return pendingFetch
}
/** Tests only: forget a call a previous test left behind. */
export const resetPendingUpdateCallsForTests = (): void => {
  pendingCheck = null
  pendingFetch = null
}

interface Props {
  /** An operator-provided lockout message (config/app `bundleLockoutMessage`), or null. */
  message?: string | null
  /** The floor the lock holds for — named back in `onAttemptEnded`. */
  bundleFloor: string | null
  /** The attempt ended WITHOUT a restart: the caller releases the lock (it lives only this long). */
  onAttemptEnded?: (floor: string | null, outcome: BundleAttemptOutcome) => void
}

/**
 * ATL-1452 — the bundle lockout. On mount it runs ONE update attempt (bundleUpdateAttempt.ts) and
 * reports how it ended; every ending but a restart releases the lock. There is no retry button to
 * leave a cashier facing: the attempt is automatic, bounded by its timeouts, and the next launch
 * tries again. Unmounting (the lock was lifted) cancels the attempt, and nothing restarts a till from
 * a screen nobody sees. Rendered by BundleGate, above the signed-in/signed-out branch.
 */
export default function BundleUpdateScreen({ message, bundleFloor, onAttemptEnded }: Props) {
  const { t } = useTranslation()
  const theme = useTheme()
  const postHog = usePostHog()
  /** The restart was issued RESTART_HINT_MS ago and this till is still here: tell the cashier. */
  const [restartHintShown, setRestartHintShown] = useState(false)
  /** Fixed for the life of the process — computed once, not on every render. */
  const runningIdentity = useMemo(() => runningBundleIdentity(), [])

  // The attempt outlives renders: it reads the latest props through this ref.
  const latest = useRef({ bundleFloor, onAttemptEnded, postHog })
  latest.current = { bundleFloor, onAttemptEnded, postHog }

  /** Telemetry never decides anything: a throwing client cannot change an outcome or skip a write. */
  const report = (method: string, extra: Record<string, unknown> = {}): void => {
    try {
      latest.current.postHog?.capture?.('version_lockout_recovery', {
        ...extra,
        method,
        lockout_type: 'hard',
        block_kind: 'bundle',
        ...bundleFloorProperties(latest.current.bundleFloor ?? null),
        app_version: APP_VERSION,
      })
    } catch {
      /* ignored */
    }
  }

  // "Shown" means SEEN: counted when this screen mounts (once per process, whatever remounts it), so
  // "shown − released" balances.
  useEffect(() => {
    if (!markBundleLockoutShown()) return
    try {
      latest.current.postHog?.capture?.('bundle_lockout_shown', {
        lockout_type: 'hard',
        ...bundleFloorProperties(latest.current.bundleFloor ?? null),
        running_at: runningIdentity.at?.toISOString() ?? null,
        source: runningIdentity.source,
      })
    } catch {
      /* telemetry never decides anything */
    }
  }, [runningIdentity])

  useEffect(() => {
    // A restart was already ISSUED in this process (this screen is a remount of the root under it):
    // there is no second attempt to run — only the cashier to tell.
    if (bundleGateProcess.restartIssued) {
      setRestartHintShown(true)
      return undefined
    }
    let active = true
    let cancelRestart: (() => void) | null = null
    let restartHint: ReturnType<typeof setTimeout> | null = null
    /** Pending retry pauses: on unmount each is cleared AND resolved, so the attempt settles. */
    const sleeps = new Map<ReturnType<typeof setTimeout>, () => void>()
    /** The attempt ends — and the lock is released — EXACTLY once, whatever throws after. */
    let ended = false
    const endOnce = (floor: string | null, outcome: BundleAttemptOutcome): void => {
      if (ended || !active) return
      ended = true
      latest.current.onAttemptEnded?.(floor, outcome)
    }
    const stillWanted = (): boolean => active

    /**
     * The live rollback's own drain-then-restart (performLiveRollback → reloadApp, ATL-888/889):
     * drain in-flight writes immediately before the teardown (capped, and disclosed if the cap
     * expires), flush telemetry, restart. Settles on every path except a successful restart.
     */
    const restart = async (): Promise<RestartResult> => {
      if (!active) return 'cancelled'
      const floor = latest.current.bundleFloor
      // Never a restart without its loop guard: with no floor there is nothing to back off, so a
      // relaunch on the same bundle could lock and restart again, forever. (Unreachable — a lock always
      // names its floor — so it is a failure, not a storage veto.)
      if (!floor) return 'restart-failed'
      let backoffWritten = false
      /** The back-off write itself — it may still land after its bounded wait timed out. */
      let backoffWrite: Promise<boolean> | null = null
      /** Exactly what this restart wrote: only THAT is ever taken back (a newer attempt's is kept). */
      const record = { floor, at: Date.now(), reason: 'restarted' as const }
      /**
       * A restart called off BEFORE it was issued: correct the metric, and take back what it spent.
       * Never after the reload was issued (an issued reload cannot be called back — it may still land,
       * and then its back-off must be there), and never after the restart settled.
       */
      let reloadIssued = false
      let settledOutcome = false
      /** Take back the 'restarted' back-off of a restart that will not happen. */
      const takeBackBackoff = async (): Promise<void> => {
        if (!backoffWrite) return
        // Wait for the write to land (bounded) BEFORE clearing — a slow write landing after the
        // clear would back off the next launch for a restart that never happened.
        await timeoutAfter(backoffWrite, STORAGE_TIMEOUT_MS, 'back-off write').catch(() => false)
        await timeoutAfter(clearBackoffRecordIf(record), STORAGE_TIMEOUT_MS, 'back-off clear').catch(
          () => {}
        )
      }
      const undo = async (): Promise<void> => {
        if (reloadIssued || settledOutcome) return
        report('restart_cancelled')
        await takeBackBackoff()
      }
      // Reported BEFORE the flush, so the event leaves with the process; a restart that is then
      // called off or vetoed sends a correcting event, so the ramp can subtract it.
      report(Platform.OS === 'web' ? 'web_reload' : 'ota_applied')
      let flushed: unknown
      try {
        flushed = latest.current.postHog?.flush?.()
      } catch {
        flushed = undefined
      }
      await timeoutAfter(Promise.resolve(flushed), TELEMETRY_FLUSH_TIMEOUT_MS, 'telemetry flush').catch(
        () => {}
      )
      if (!active) {
        await undo()
        return 'cancelled'
      }
      // What the restart consumes is written BEFORE the drain — the drain must sit immediately before
      // the teardown (ATL-889), with nothing slow in between for new writes to start in.
      // The ONE record, on both platforms: a relaunch that comes back on the same bundle (native:
      // expo-updates rolled the update back; web: the hosted build is the newest there is) is not
      // locked again for UPDATE_BACKOFF_MS — never a restart loop.
      backoffWrite = writeBackoffRecord(record)
      await timeoutAfter(backoffWrite, STORAGE_TIMEOUT_MS, 'back-off write')
        .then((ok) => {
          backoffWritten = ok
        })
        .catch(() => {})
      if (!backoffWritten) {
        // Never a restart without its loop guard, on either platform (the gate only locks where a
        // back-off could be written, so this is storage failing mid-attempt): nothing restarts.
        report('restart_vetoed')
        await takeBackBackoff() // a write that timed out may still land
        return 'vetoed'
      }
      if (!active) {
        await undo()
        return 'cancelled'
      }
      return new Promise((resolve) => {
        let settled = false
        const done = (r: RestartResult): void => {
          if (settled) return
          settled = true
          settledOutcome = true
          cancelRestart = null // a settled restart has nothing left to call off
          if (restartHint) clearTimeout(restartHint)
          resolve(r)
        }
        // The live rollback's own sequence (performLiveRollback → reloadApp, ATL-888/889): drain
        // in-flight writes (capped; a cap expiry is DISCLOSED), re-check cancellation, restart.
        const cancel = performLiveRollback({
          notify: () => {},
          graceMs: 0,
          awaitDrain: () => waitForWritesToDrain({ capMs: LIVE_ROLLBACK_DRAIN_CAP_MS }),
          label: 'bundle-lockout',
          drainExpiredMessage:
            'restarting into the update with writes STILL IN FLIGHT — the drain cap expired. Their ' +
            'Supabase mirrors may be lost without a rejection (ATL-889); reconcile this org',
          reloadFailedMessage:
            'the restart into the update failed — this till is still on the bundle below the floor',
          // (Never reached once cancelled: every unmount calls cancelRestart, which cancels this first.)
          reload: () => {
            reloadIssued = true
            // From here the lock is final for the process: nothing releases it (useBundleGate).
            bundleGateProcess.restartIssued = true
            reloadApp({
              label: 'bundle-lockout',
              logger: {
                error: (...args: unknown[]) => {
                  console.error(...args)
                  // It demonstrably did not happen: the lock may end with the attempt again.
                  bundleGateProcess.restartIssued = false
                  // WEB: nothing happened at all (location.reload threw), so the back-off it wrote
                  // must not stop the next launch from trying. (Native: the update IS downloaded
                  // and applies on the next launch — its back-off stays, against a relaunch loop.)
                  if (Platform.OS === 'web' && backoffWritten) {
                    void timeoutAfter(clearBackoffRecordIf(record), STORAGE_TIMEOUT_MS, 'back-off clear')
                      .catch(() => {})
                      .finally(() => done('restart-failed'))
                    return
                  }
                  done('restart-failed')
                },
              },
            })
            // Only for a restart still pending: one that already failed (a synchronous throw) is over.
            if (!settled) {
              restartHint = setTimeout(() => setRestartHintShown(true), RESTART_HINT_MS)
            }
          },
        })
        cancelRestart = () => {
          cancel()
          // Once the reload is issued there is nothing to call off: leave it (and its back-off) be —
          // but SETTLE the promise, so nothing waits on it (and its closures) forever.
          if (reloadIssued) {
            done('cancelled')
            return
          }
          void undo().finally(() => done('cancelled'))
        }
      })
    }

    let lastFailure: { stage: 'check' | 'fetch'; error: string } | null = null
    void runBundleUpdateAttempt({
      platform: Platform.OS,
      checkForUpdate: joinCheck,
      fetchUpdate: joinFetch,
      restart,
      stillWanted,
      sleep: (ms) =>
        new Promise<void>((r) => {
          const timer = setTimeout(() => {
            sleeps.delete(timer)
            r()
          }, ms)
          sleeps.set(timer, r)
        }),
      onTryFailed: (stage, error) => {
        lastFailure = { stage, error: String(error) }
        report('ota_failed', { stage })
      },
    })
      .then(async (outcome) => {
        if (!active || outcome === 'cancelled') return
        report(`attempt_${outcome.replace(/-/g, '_')}`, lastFailure ? { stage: lastFailure.stage } : {})
        const floor = latest.current.bundleFloor
        if ((outcome === 'no-newer-update' || outcome === 'update-failed') && floor) {
          // Later launches skip this floor for UPDATE_BACKOFF_MS — they would only find nothing newer,
          // or fail the same way (a network that reaches Firestore but never the EAS CDN).
          await timeoutAfter(
            writeBackoffRecord({ floor, at: Date.now(), reason: outcome }),
            STORAGE_TIMEOUT_MS,
            'back-off write'
          ).catch(() => {})
        }
        endOnce(floor, outcome)
      })
      .catch((error: unknown) => {
        // The attempt never throws by construction; if it did, the lock must still be released.
        console.error('bundle lockout: update attempt threw', error)
        endOnce(latest.current.bundleFloor, 'update-failed')
      })

    return () => {
      active = false
      cancelRestart?.()
      if (restartHint) clearTimeout(restartHint)
      // Resolved, not just cleared: the attempt wakes, sees it is no longer wanted, and settles
      // ('cancelled') — instead of pending forever with its closures.
      sleeps.forEach((resolve, timer) => {
        clearTimeout(timer)
        resolve()
      })
      sleeps.clear()
    }
    // One attempt per mount and floor: a new floor (the listener re-pointed the lock) is a new attempt.
  }, [bundleFloor])

  const shownMessage = message || t('blockedScreen.bundleMessage')

  return (
    <Layout style={styles.container}>
      <View style={styles.content}>
        <Icon name="refresh-outline" style={styles.icon} fill={theme['color-primary-500']} />
        <Text category="h4" style={styles.title}>
          {t('blockedScreen.bundleTitle')}
        </Text>
        <Text category="p1" style={styles.message}>
          {shownMessage}
        </Text>
        <View style={styles.spinner}>
          <Spinner size="large" />
        </View>
        {restartHintShown && (
          <Text category="p2" status="warning" style={styles.message}>
            {t('blockedScreen.restartHint')}
          </Text>
        )}
        <Text category="c2" appearance="hint" style={styles.version}>
          {t('blockedScreen.currentVersion')}: {APP_VERSION}
        </Text>
        {/* What support needs from a screenshot: the running bundle and the floor it is below. */}
        <Text category="c2" appearance="hint" style={styles.version}>
          {t('blockedScreen.bundleIdentity')}: {runningIdentity.at?.toISOString() ?? '—'}
          {' < '}
          {String(bundleFloor ?? '—')}
        </Text>
      </View>
    </Layout>
  )
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 24 },
  icon: { width: 80, height: 80, marginBottom: 24 },
  title: { textAlign: 'center', marginBottom: 12 },
  message: { textAlign: 'center', marginBottom: 16, paddingHorizontal: 16 },
  spinner: { marginBottom: 24 },
  version: { textAlign: 'center', marginBottom: 4 },
})
